/**
 * <tv-panel-contents>: folder tabs, one per variant.
 * - Active tab follows the variant selected in the buy box (tv:variant-change from <tv-pdp>).
 * - Clicking a tab only changes what is shown; it does not change the selection.
 * - "Choose this panel" selects the variant in <tv-pdp> and scrolls back to the selector.
 *   On the tab of the already-selected variant the button reads "Continue with this panel".
 */
class TvPanelContents extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[data-tv-ptab]'));
    this.panels = Array.from(this.querySelectorAll('[data-tv-ppanel]'));
    this.chooseLabel = this.dataset.chooseLabel || 'Choose this panel';
    this.continueLabel = this.dataset.continueLabel || 'Continue with this panel';
    this.selectedId = null;

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.activate(tab.dataset.tvPtab));
      tab.addEventListener('keydown', (event) => this.onKey(event, tab));
    });

    this.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-tv-choose]');
      if (!btn || btn.disabled) return;
      this.choose(btn.dataset.tvChoose);
    });

    this.onChange = (event) => {
      const v = event.detail && event.detail.variant;
      if (v) this.sync(v.id);
    };
    document.addEventListener('tv:variant-change', this.onChange);

    // <tv-pdp> may have fired its initial event before this element was defined.
    const pdp = document.querySelector('tv-pdp');
    const current = (pdp && pdp.variant && pdp.variant.id) || this.dataset.selected;
    this.sync(current);
  }

  disconnectedCallback() {
    document.removeEventListener('tv:variant-change', this.onChange);
  }

  /* Selection changed in the buy box: show that panel and relabel the buttons. */
  sync(id) {
    if (!id) return;
    this.selectedId = String(id);
    this.activate(this.selectedId);
    this.querySelectorAll('[data-tv-choose]').forEach((btn) => {
      const label = btn.querySelector('[data-tv-choose-label]');
      if (!label || btn.disabled) return;
      label.textContent = btn.dataset.tvChoose === this.selectedId ? this.continueLabel : this.chooseLabel;
    });
  }

  activate(id) {
    id = String(id);
    if (!this.panels.some((p) => p.dataset.tvPpanel === id)) return;
    this.tabs.forEach((tab) => {
      const on = tab.dataset.tvPtab === id;
      tab.classList.toggle('is-active', on);
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.tabIndex = on ? 0 : -1;
    });
    this.panels.forEach((panel) => {
      const on = panel.dataset.tvPpanel === id;
      panel.classList.toggle('is-active', on);
      panel.hidden = !on;
    });
  }

  onKey(event, tab) {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    const i = this.tabs.indexOf(tab);
    const next = this.tabs[(i + (event.key === 'ArrowRight' ? 1 : this.tabs.length - 1)) % this.tabs.length];
    if (!next) return;
    this.activate(next.dataset.tvPtab);
    next.focus();
  }

  choose(id) {
    const pdp = document.querySelector('tv-pdp');
    if (pdp && typeof pdp.setVariant === 'function') pdp.setVariant(id);
    const target = document.querySelector('[data-tv-variants-wrap]') || pdp;
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

if (!customElements.get('tv-panel-contents')) customElements.define('tv-panel-contents', TvPanelContents);
